import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ProductSearchInput from "./add-item/ProductSearchInput";
import NewProductForm from "./add-item/NewProductForm";
import ProductGrid from "./add-item/ProductGrid";
import SelectedProductCard from "./add-item/SelectedProductCard";
import FilterButtons from "./FilterButtons";
import { useAddItemDialog } from "@/hooks/shopping/useAddItemDialog";

interface AddItemDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  listId: string;
  rapidInsert?: boolean;
  onSuccess?: () => void;
}

const AddItemDialog = ({ isOpen, onOpenChange, listId, rapidInsert = false, onSuccess }: AddItemDialogProps) => {
  const state = useAddItemDialog({ isOpen, onOpenChange, listId, rapidInsert, onSuccess });
  const [showFilters, setShowFilters] = React.useState(false);
  const {
    theme,
    handleOpenChange,
    searchTerm,
    setSearchTerm,
    groupedProducts,
    filter,
    setFilter,
    showOfflineMessage,
    isLoading,
    selectedProduct,
    setSelectedProduct,
    quantity,
    setQuantity,
    quantityPresets,
    unit,
    setUnit,
    category,
    setCategory,
    handleProductSelect,
    handleAdd,
    handleAddClose,
    handleCreateProduct,
    isCreating,
    scrollRef,
    addedProductIds,
    addedProductsLocal,
    categoryItems,
    isOnline,
    shoppingItems,
    forceNewProductForm,
  } = state;

  const dialogTitle = selectedProduct || forceNewProductForm ? "Crea Nuovo Prodotto" : "Aggiungi Prodotto";
  const isSearchMode = !selectedProduct && !forceNewProductForm;
  // Mantiene la modale dell'altezza piena sia in modalità ricerca che quando si crea un nuovo articolo
  const dialogHeight = selectedProduct ? "60vh" : "90vh";

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        // Garantisce un'altezza minima quando si crea un nuovo prodotto,
        // in linea con le modali di creazione negozi o categorie
        className={`w-full flex flex-col ${forceNewProductForm ? 'min-h-[60vh]' : ''}`}
        containerClassName={`flex flex-col ${forceNewProductForm ? 'pb-0' : ''}`}
        scrollRef={scrollRef}
        scrollOptions={{ offsetTop: 120, offsetBottom: 120 }}
        style={{
          borderRadius: theme.borderRadius || '0.5rem',
          backgroundColor: theme.cardBackgroundColor,
          color: theme.sectionTextColor,
          transition: `all ${theme.transitionSpeed || '0.2s'} ease`,
          maxHeight: dialogHeight,
          height: dialogHeight
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="mb-1">
          <DialogTitle className="text-2xl font-bold">{dialogTitle}</DialogTitle>
          <DialogDescription className="sr-only" style={{ color: theme.sectionTitleColor }}>
            Aggiungi un prodotto alla lista
          </DialogDescription>
        </DialogHeader>

        <div className="mb-2">
          <ProductSearchInput
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            placeholder="Cerca prodotto..."
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            filtersVisible={showFilters}
            filtersDisabled={forceNewProductForm}
            style={{
              borderRadius: theme.inputBorderRadius || theme.borderRadius || '0.375rem',
              transition: `all ${theme.transitionSpeed || '0.2s'} ease`
            }}
          />
        </div>

        {showFilters && (
          <FilterButtons
            currentFilter={filter}
            setFilter={setFilter}
            className="mb-2"
            style={{ borderRadius: theme.buttonBorderRadius || '0.375rem' }}
          />
        )}

        {!isOnline && (
          <div className="mb-2 p-2 rounded-lg" style={{ backgroundColor: theme.cardBackgroundColor, color: theme.warningColor }}>
            <p className="text-xs">Modalità offline - Lista prodotti limitata</p>
          </div>
        )}

        <div
          ref={scrollRef}
          // Evita che la form collassi quando la tastiera è aperta
          className={`flex-1 overflow-y-auto overflow-x-hidden pr-2 ${forceNewProductForm ? 'min-h-[60vh]' : 'pb-safe'}`}
        >
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <p>Caricamento prodotti...</p>
            </div>
          ) : showOfflineMessage ? (
            <div className="flex flex-col justify-center items-center h-32 text-center">
              <p className="mb-2" style={{ color: theme.sectionTextColor }}>Nessun prodotto disponibile offline</p>
              <p className="text-sm" style={{ color: theme.sectionTextColor }}>Torna online per accedere alla lista completa</p>
            </div>
          ) : selectedProduct ? (
            <SelectedProductCard
              product={selectedProduct}
              quantity={quantity}
              setQuantity={setQuantity}
              unit={unit}
              quantityPresets={quantityPresets}
              onChange={() => setSelectedProduct(null)}
              onAdd={handleAdd}
              onAddClose={handleAddClose}
              onReset={() => { setSearchTerm(''); setSelectedProduct(null); setQuantity('1'); setUnit('pz'); }}
              onSubmit={() => handleAdd()}
              onCancel={() => handleOpenChange(false)}
              style={{ borderRadius: theme.borderRadius || '0.375rem', backgroundColor: theme.cardBackgroundColor || theme.backgroundColor }}
            />
          ) : forceNewProductForm ? (
            <NewProductForm
              name={searchTerm}
              setName={setSearchTerm}
              category={category}
              setCategory={setCategory}
              unit={unit}
              setUnit={setUnit}
              onCreateProduct={handleCreateProduct}
              isLoading={isCreating}
              style={{ borderRadius: theme.borderRadius || '0.375rem', backgroundColor: theme.cardBackgroundColor || theme.backgroundColor }}
            />
          ) : (
            <ProductGrid
              key={addedProductIds.join('-')}
              groupedProducts={groupedProducts}
              categories={categoryItems}
              onProductSelect={handleProductSelect}
              addedProductIds={addedProductIds}
              checkedProductIds={shoppingItems.filter(i => i.checked).map(i => i.product_id)}
              recentlyAddedIds={addedProductsLocal}
              style={{ borderRadius: theme.borderRadius || '0.375rem' }}
            />
          )}
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default AddItemDialog;
